import React, { useContext, useState } from 'react';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { postRequest } from '../../../api/ApiController';
import UtilContext from '../../../components/UtilContext';
import { LocaleContext } from '../../../components/LocaleContext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { getTimeUnitDays, getSemaforColorOptions, SEMAFOR_COLOR_VALUES, getSemaforColorClass } from '../../../utils/moduleSettings';

function getSetting(module_settings, key) {
  const row = (module_settings || []).find(s => s.module === 'maintenance' && s.key === key);
  return row ? row.value : '5';
}

/** Hodnota z DB môže byť názov farby alebo starý hex – pre Dropdown vrátime platný názov alebo defaultName. */
function effectiveColorValue(value, defaultName) {
  const v = String(value || '').trim();
  if (SEMAFOR_COLOR_VALUES.includes(v)) return v;
  return defaultName;
}

function ColorSelectRow({ label, setting, defaultName, dispatch, toast, t, colorOptions }) {
  const [saving, setSaving] = useState(false);
  const currentValue = effectiveColorValue(setting?.value, defaultName);

  async function save(newVal) {
    if (!setting || newVal === currentValue) return;
    setSaving(true);
    try {
      await postRequest(`/api/module_settings/update/${setting.id}`, { value: newVal }, true);
      dispatch({ type: 'update_module_setting', id: setting.id, value: newVal });
      toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Saved'), life: 2000, detail: t('ServiceGeneralSettings.ColorChanged') });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 3000, detail: e?.message || t('ServiceTask.SaveFailed') });
    } finally {
      setSaving(false);
    }
  }

  if (!setting) return null;
  const colorClass = getSemaforColorClass(currentValue);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[minmax(200px,1fr)_220px] gap-3 items-center py-1.5">
      <label className="text-sm font-medium text-gray-800 truncate">{label}</label>
      <div className="flex items-center gap-2 shrink-0 min-w-0">
        <span className={`inline-block w-5 h-5 rounded-full shrink-0 ${colorClass}`} title={currentValue} aria-hidden />
        <Dropdown
          value={currentValue}
          options={colorOptions}
          optionLabel="label"
          optionValue="value"
          onChange={(e) => save(e.value)}
          className="w-[180px] border border-gray-400 rounded"
          placeholder={t('ServiceGeneralSettings.Color')}
          disabled={saving}
        />
      </div>
    </div>
  );
}

export default function ServiceGeneralSettings() {
  const [state, dispatch] = useContext(DatabaseContext);
  const { toast } = useContext(UtilContext);
  const { t } = useContext(LocaleContext);
  const colorOptions = getSemaforColorOptions(t);

  const module_settings = state.module_settings || [];
  const daysToDeadline = getSetting(module_settings, 'days_to_deadline') || '5';
  const settingDaysToDeadline = module_settings.find(s => s.module === 'maintenance' && s.key === 'days_to_deadline');
  const undefinedStateDeadlineDays = getSetting(module_settings, 'undefined_state_deadline_days') || '3';
  const settingUndefinedStateDeadline = module_settings.find(s => s.module === 'maintenance' && s.key === 'undefined_state_deadline_days');
  const unitDays = getTimeUnitDays(module_settings);

  const getSettingByKey = (key) => module_settings.find(s => s.module === 'maintenance' && s.key === key);

  async function changeDaysToDeadline(value) {
    if (!settingDaysToDeadline) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 5000, detail: t('ServiceGeneralSettings.SettingNotFound') });
      return;
    }
    const val = String(Math.max(1, Math.min(99, value || 5)));
    if (val === daysToDeadline) return;
    try {
      await postRequest(`/api/module_settings/update/${settingDaysToDeadline.id}`, { value: val }, true);
      dispatch({ type: 'update_module_setting', id: settingDaysToDeadline.id, value: val });
      toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), life: 3000, detail: t('ServiceGeneralSettings.ValueChanged') });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 3000, detail: e?.message || t('ServiceTask.SaveFailed') });
    }
  }

  async function changeUndefinedStateDeadlineDays(value) {
    if (!settingUndefinedStateDeadline) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 5000, detail: t('ServiceGeneralSettings.SettingNotFound') });
      return;
    }
    const val = String(Math.max(1, Math.min(99, value || 3)));
    if (val === undefinedStateDeadlineDays) return;
    try {
      await postRequest(`/api/module_settings/update/${settingUndefinedStateDeadline.id}`, { value: val }, true);
      dispatch({ type: 'update_module_setting', id: settingUndefinedStateDeadline.id, value: val });
      toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), life: 3000, detail: t('ServiceGeneralSettings.ValueChanged') });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 3000, detail: e?.message || t('ServiceTask.SaveFailed') });
    }
  }

  const indicatorKeys = [
    { key: 'status_indicator_red', label: t('ServiceTask.Status.Overdue'), defaultName: 'red' },
    { key: 'status_indicator_orange', label: t('ServiceTask.Status.DeadlineSoon'), defaultName: 'orange' },
    { key: 'status_indicator_green', label: t('ServiceTask.Status.OK'), defaultName: 'green' },
  ];
  const graphDeadlineKeys = [
    { key: 'graph_deadline_red', label: t('ServiceTask.Status.Overdue'), defaultName: 'red' },
    { key: 'graph_deadline_orange', label: t('ServiceTask.Status.DeadlineSoon'), defaultName: 'orange' },
    { key: 'graph_deadline_green', label: t('ServiceTask.Status.OK'), defaultName: 'green' },
  ];
  const graphStatesDefaults = ['orange', 'blue', 'blue', 'green', 'blue', 'gray'];
  const graphPeriodDefaults = ['blue', 'red', 'green'];
  const graphStatesLabels = [t('ServiceTask.Status.WaitingApproval'), t('ServiceTask.Status.Approved'), t('ServiceTask.Status.InProgress'), t('ServiceTask.Status.Done'), t('ServiceTask.Status.WaitingInspection'), t('ServiceTask.Status.Reopened')];
  const graphPeriodLabels = [t('ServiceTask.Period.AddedTickets'), t('ServiceTask.Period.UnresolvedTickets'), t('ServiceTask.Period.ClosedTickets')];

  return (
    <div className="relative flex flex-col h-[calc(100vh-137px)] bg-gray-100 overflow-y-auto overflow-x-hidden">
      <div className="p-4 w-full">
        <h1 className="text-3xl font-semibold text-left text-zinc-900 mb-4">
          {t('ServiceGeneralSettings.Title')}
        </h1>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 w-full">
          {/* Stĺpec 1: Indikátor stavu + Farby indikátora */}
          <div className="p-6 bg-white rounded-md shadow-md">
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">
              {t('ServiceGeneralSettings.StatusIndicatorTitle')} - {unitDays} {t('ServiceTask.DaysToDeadlineSuffix')}
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              {t('ServiceGeneralSettings.OrangeIndicatorHint')}
            </p>
            {settingDaysToDeadline && (
              <div className="pl-4 border-l-2 border-gray-200 mb-6">
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-center">
                  <label className="text-sm font-medium text-gray-800">
                    {t('ServiceGeneralSettings.DaysToDeadlineCount')} ({t('ServiceGeneralSettings.OrangeIndicator')})
                  </label>
                  <InputNumber
                    value={parseInt(daysToDeadline, 10) || 5}
                    onValueChange={(e) => changeDaysToDeadline(e.value)}
                    min={1}
                    max={99}
                    showButtons
                    className="border border-gray-300 rounded-md"
                    size={6}
                  />
                </div>
              </div>
            )}
            {settingUndefinedStateDeadline && (
              <div className="pl-4 border-l-2 border-gray-200 mb-6">
                <p className="text-sm text-gray-500 mb-3">
                  {t('ServiceGeneralSettings.UndefinedStateDeadlineHint')}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-center">
                  <label className="text-sm font-medium text-gray-800">
                    {t('ServiceGeneralSettings.UndefinedStateDeadlineLabel')}
                  </label>
                  <InputNumber
                    value={parseInt(undefinedStateDeadlineDays, 10) || 3}
                    onValueChange={(e) => changeUndefinedStateDeadlineDays(e.value)}
                    min={1}
                    max={99}
                    showButtons
                    className="border border-gray-300 rounded-md"
                    size={6}
                  />
                </div>
              </div>
            )}

            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200 mt-6">
              {t('ServiceGeneralSettings.StatusIndicatorColors')}
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              {t('ServiceGeneralSettings.StatusIndicatorColorsHint')}
            </p>
            <div className="pl-4 flex flex-col gap-1 border-l-2 border-gray-200">
              {indicatorKeys.map(({ key, label, defaultName }) => (
                <ColorSelectRow
                  key={key}
                  label={label}
                  setting={getSettingByKey(key)}
                  defaultName={defaultName}
                  t={t}
                  dispatch={dispatch}
                  toast={toast}
                  colorOptions={colorOptions}
                />
              ))}
            </div>
          </div>

          {/* Stĺpec 2: Graf obdobia + Graf Po termíne */}
          <div className="p-6 bg-white rounded-md shadow-md">
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">
              {t('ServiceGeneralSettings.GraphColorsTitle')}
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              {t('ServiceGeneralSettings.GraphColorsHint')}
            </p>

            <div className="mb-6">
              <h3 className="text-base font-semibold text-zinc-700 mb-2">{t('ServiceGeneralSettings.PeriodGraph')}</h3>
              <div className="pl-4 flex flex-col gap-1 border-l-2 border-gray-200">
                {[1, 2, 3].map((i) => (
                  <ColorSelectRow
                    key={`graph_period_${i}`}
                    label={graphPeriodLabels[i - 1]}
                    setting={getSettingByKey(`graph_period_${i}`)}
                    defaultName={graphPeriodDefaults[i - 1]}
                    t={t}
                    dispatch={dispatch}
                    toast={toast}
                    colorOptions={colorOptions}
                  />
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-base font-semibold text-zinc-700 mb-2">{t('ServiceGeneralSettings.DeadlineGraph')}</h3>
              <div className="pl-4 flex flex-col gap-1 border-l-2 border-gray-200">
                {graphDeadlineKeys.map(({ key, label, defaultName }) => (
                  <ColorSelectRow
                    key={key}
                    label={label}
                    setting={getSettingByKey(key)}
                    defaultName={defaultName}
                    t={t}
                    dispatch={dispatch}
                    toast={toast}
                    colorOptions={colorOptions}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Stĺpec 3: Graf stavov */}
          <div className="p-6 bg-white rounded-md shadow-md md:col-span-2 xl:col-span-1">
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">
              {t('ServiceGeneralSettings.PreviewGraphColors')}
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              {t('ServiceGeneralSettings.PreviewGraphColorsHint')}
            </p>
            <h3 className="text-base font-semibold text-zinc-700 mb-2">{t('ServiceGeneralSettings.StatusGraph')}</h3>
            <div className="pl-4 flex flex-col gap-1 border-l-2 border-gray-200">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <ColorSelectRow
                  key={`graph_states_${i}`}
                  label={graphStatesLabels[i - 1]}
                  setting={getSettingByKey(`graph_states_${i}`)}
                  defaultName={graphStatesDefaults[i - 1]}
                  t={t}
                  dispatch={dispatch}
                    toast={toast}
                    colorOptions={colorOptions}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
